import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Outlet, Navigate } from 'react-router-dom';
import { ChatProvider, LoadingState } from '@lykbl/ui';
import { DevModeProvider, DevModeIndicator, DevModeAuthGate } from '@lykbl/dev-tools';
import { AuthProvider } from './contexts/AuthContext';
import SiteNav from './components/redesign/SiteNav';
import SiteFooter from './components/redesign/SiteFooter';
import MarketingAnalytics from './components/MarketingAnalytics';
import RouteSeo from './components/RouteSeo';
import { DEV_TOOLS_ENABLED } from './lib/dev-tools-enabled';

// Lazy load pages
const Home = lazy(() => import('./pages/Home'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const Learn = lazy(() => import('./pages/Learn'));
const Help = lazy(() => import('./pages/Help'));
const About = lazy(() => import('./pages/About'));
const NotFound = lazy(() => import('./pages/NotFound'));

// /waitlist: request-access form while the public beta is closed, a pointer to
// lykbl.ai/signup once it opens (build flag — see lib/signup.ts)
const Waitlist = lazy(() => import('./pages/Waitlist'));

// Legal pages
const Terms = lazy(() => import('./pages/legal/Terms'));
const Privacy = lazy(() => import('./pages/legal/Privacy'));

// Auth lives entirely on the app (lykbl.ai). These routes only hand off.
const RedirectToApp = lazy(() => import('./pages/auth/RedirectToApp'));

// Scroll-flight experience — THE landing page at `/` (statically imported so
// the entry bundle mounts it with no lazy-chunk hop; index.html has already
// begun preloading its first scene by then). /flight-a|b stay as noindexed
// aliases for the Dawn/Night toggle.
import FlightLanding from './pages/FlightLanding';
import FlightA from './pages/FlightA';
import FlightB from './pages/FlightB';

// Main layout with redesigned nav and footer
function MainLayout() {
  return (
    <>
      <a href="#main" className="skip-link">Skip to main content</a>
      <SiteNav />
      <main id="main" className="min-h-screen">
        <Outlet />
      </main>
      <SiteFooter />
    </>
  );
}

// Auth layout without Navbar/Footer
function AuthLayout() {
  return (
    <main className="min-h-screen">
      <Outlet />
    </main>
  );
}

/**
 * The router-agnostic app tree. The browser wraps it in <BrowserRouter> (App
 * below); the build-time prerender (entry-server.tsx) wraps it in a
 * <StaticRouter> with `devTools={false}` so the markup matches hydration.
 */
export function MarketingApp({ devTools }: { devTools: boolean }) {
  // Get chat API URL from environment
  const chatApiUrl = import.meta.env.VITE_CHAT_API_URL || '/functions/v1/chat';
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

  const app = (
          <AuthProvider>
            <MarketingAnalytics />
            {/* SEO defaults — must precede <Routes> so page <Helmet>s override it */}
            <RouteSeo />
            <ChatProvider mode="marketing" chatApiUrl={chatApiUrl} supabaseAnonKey={supabaseAnonKey}>
              <Suspense fallback={<LoadingState text="Loading..." fullScreen />}>
                <Routes>
                  {/* Auth hands off to the app — no auth UI on the marketing site */}
                  <Route element={<AuthLayout />}>
                    <Route path="/auth" element={<RedirectToApp />} />
                    <Route path="/auth/callback" element={<RedirectToApp />} />
                  </Route>

                  {/* The landing page IS the flight — bare routes, the engine
                      renders its own chrome. Variant auto-resolves (?v= >
                      sticky choice > system appearance). /flight kept as a
                      legacy alias for links in the wild. */}
                  <Route path="/" element={<FlightLanding />} />
                  <Route path="/flight" element={<Navigate to="/" replace />} />
                  <Route path="/flight-a" element={<FlightA />} />
                  <Route path="/flight-b" element={<FlightB />} />

                  {/* Main routes with navbar/footer. The classic overview page
                      moved from `/` to /home ("Find out more" from the flight). */}
                  <Route element={<MainLayout />}>
                    <Route path="/home" element={<Home />} />
                    <Route path="/help" element={<Help />} />
                    <Route path="/about" element={<About />} />

                    {/* Unlisted for MVP (reachable by URL, noindex, no nav/footer links) */}
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:id" element={<BlogPost />} />
                    <Route path="/learn" element={<Learn />} />

                    {/* Request beta access (closed) / public beta pointer (open) */}
                    <Route path="/waitlist" element={<Waitlist />} />
                    {/* Campaign-Connect is out of MVP scope — hide the brand waitlist */}
                    <Route path="/brands/waitlist" element={<Navigate to="/" replace />} />

                    {/* Out of MVP scope — redirected (2026 redesign) */}
                    <Route path="/colab" element={<Navigate to="/" replace />} />
                    <Route path="/campaign-connect" element={<Navigate to="/" replace />} />
                    <Route path="/creators" element={<Navigate to="/" replace />} />
                    <Route path="/brands" element={<Navigate to="/" replace />} />
                    <Route path="/pricing" element={<Navigate to="/" replace />} />
                    <Route path="/CES2026" element={<Navigate to="/" replace />} />
                    <Route path="/join-creator" element={<Navigate to="/waitlist" replace />} />
                    
                    {/* Legal */}
                    <Route path="/legal/terms" element={<Terms />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/legal/privacy" element={<Navigate to="/privacy" replace />} />
                    <Route path="/ai-amp" element={<Navigate to="https://lykbl.ai" replace />} />
                    
                    {/* Legacy footer links (avoid 404s from older deployments / cached builds) */}
                    <Route path="/careers" element={<Navigate to="/about" replace />} />
                    <Route path="/press" element={<Navigate to="/" replace />} />
                    <Route path="/press-kit" element={<Navigate to="/" replace />} />
                    <Route path="/contact" element={<Navigate to="/help" replace />} />
                    
                    {/* 404 */}
                    <Route path="*" element={<NotFound />} />
                  </Route>
                </Routes>
              </Suspense>
              
              {/* Lumi chat widget hidden for now (product decision 2026-07-18). */}
              
              {/* Dev mode indicator (only shows on localhost/staging) */}
              {devTools && <DevModeIndicator />}
            </ChatProvider>
          </AuthProvider>
  );

  if (!devTools) return app;

  return (
    <DevModeProvider appType="marketing">
      <DevModeAuthGate>{app}</DevModeAuthGate>
    </DevModeProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <MarketingApp devTools={DEV_TOOLS_ENABLED} />
    </BrowserRouter>
  );
}

export default App;
